import React, { useEffect, useMemo, useState } from 'react';
import { FiChevronLeft, FiChevronRight, FiDownload, FiSearch } from 'react-icons/fi';
import { graphqlRequest } from '../utils/graphqlRequest';

const GET_INVOICES = `query { getMyBillingInvoices }`;
const PAGE_SIZE = 10;
const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'paid', label: 'Paid' },
  { id: 'failed', label: 'Failed' },
  { id: 'refunded', label: 'Refunded' },
];
const STATUS = {
  paid: { label: 'Paid', className: 'bs-badge--green' },
  failed: { label: 'Failed', className: 'bs-badge--red' },
  refunded: { label: 'Refunded', className: 'bs-badge--grey' },
  open: { label: 'Due', className: 'bs-badge--amber' },
  void: { label: 'Void', className: 'bs-badge--grey' },
  uncollectible: { label: 'Uncollectible', className: 'bs-badge--red' },
};

const shortDate = (iso, withYear = true) =>
  new Date(iso).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', ...(withYear ? { year: 'numeric' } : {}) });

const describe = (inv) =>
  inv.type === 'subscription' && inv.periodStart
    ? `${shortDate(inv.periodStart, false)} – ${shortDate(inv.periodEnd)}`
    : `${inv.description} · ${shortDate(inv.date)}`;

const csvCell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;

export default function BillingInvoices({ formatMoney }) {
  const [invoices, setInvoices] = useState(null);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    graphqlRequest(GET_INVOICES)
      .then((res) => setInvoices(res?.getMyBillingInvoices || []))
      .catch((e) => setError(e?.message || 'Failed to load invoices.'));
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const fromTs = from ? new Date(`${from}T00:00:00`).getTime() : -Infinity;
    const toTs = to ? new Date(`${to}T23:59:59`).getTime() : Infinity;
    return (invoices || []).filter((inv) => {
      const ts = new Date(inv.date).getTime();
      return (
        (filter === 'all' || inv.status === filter) &&
        (!q || String(inv.number).toLowerCase().includes(q)) &&
        ts >= fromTs &&
        ts <= toTs
      );
    });
  }, [invoices, filter, search, from, to]);

  useEffect(() => setPage(1), [filter, search, from, to]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const rows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const exportCsv = () => {
    const header = ['Invoice no.', 'Type', 'Billing period', 'Amount', 'Tax', 'Total', 'Currency', 'Status', 'Date'];
    const lines = filtered.map((inv) =>
      [inv.number, inv.type, describe(inv), inv.amount.toFixed(2), inv.tax.toFixed(2), inv.total.toFixed(2),
        inv.currency.toUpperCase(), STATUS[inv.status]?.label || inv.status, inv.date.slice(0, 10)]
        .map(csvCell)
        .join(','),
    );
    const blob = new Blob([[header.map(csvCell).join(','), ...lines].join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = Object.assign(document.createElement('a'), { href: url, download: 'sas-invoices.csv' });
    a.click();
    URL.revokeObjectURL(url);
  };

  if (error) return <p className="bs-text-danger">{error}</p>;
  if (!invoices) return <p className="bs-muted">Loading invoices…</p>;

  return (
    <section className="bs-card bs-invoices">
      <div className="bs-plan-head bs-invoices-head">
        <div>
          <p className="bs-eyebrow">Billing history</p>
          <h4 className="bs-staff-title">Invoices</h4>
          <p className="bs-muted bs-small">View, download and manage your subscription invoices.</p>
        </div>
        <button type="button" className="bs-btn bs-btn--icon" onClick={exportCsv} disabled={!filtered.length}>
          <FiDownload /> Export CSV
        </button>
      </div>

      <div className="bs-inv-toolbar">
        <div className="bs-toggle" role="tablist" aria-label="Invoice status">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              role="tab"
              aria-selected={filter === f.id}
              className={filter === f.id ? 'active' : ''}
              onClick={() => setFilter(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="bs-inv-filters">
          <label className="bs-search">
            <FiSearch aria-hidden="true" />
            <input
              type="search"
              placeholder="Search invoice number"
              aria-label="Search invoice number"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
          <div className="bs-daterange">
            <input type="date" aria-label="From date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} />
            <span>–</span>
            <input type="date" aria-label="To date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
          </div>
        </div>
      </div>

      <div className="bs-table-wrap">
        <table className="bs-table bs-inv-table">
          <thead>
            <tr>
              <th>Invoice no.</th>
              <th>Type</th>
              <th>Billing period</th>
              <th className="bs-num">Amount</th>
              <th className="bs-num">GST</th>
              <th className="bs-num">Total</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={8} className="bs-empty">
                  {invoices.length ? 'No invoices match your filters.' : 'No invoices yet.'}
                </td>
              </tr>
            ) : (
              rows.map((inv) => {
                const status = STATUS[inv.status] || { label: inv.status, className: 'bs-badge--grey' };
                return (
                  <tr key={inv.id}>
                    <td><strong>{inv.number}</strong></td>
                    <td>
                      <span className={`bs-badge ${inv.type === 'subscription' ? 'bs-badge--purple' : 'bs-badge--blue'}`}>
                        {inv.type === 'subscription' ? 'Subscription' : 'One-off'}
                      </span>
                    </td>
                    <td className="bs-muted">{describe(inv)}</td>
                    <td className="bs-num">{formatMoney(inv.amount, inv.currency)}</td>
                    <td className="bs-num">{formatMoney(inv.tax, inv.currency)}</td>
                    <td className="bs-num"><strong>{formatMoney(inv.total, inv.currency)}</strong></td>
                    <td><span className={`bs-badge ${status.className}`}>{status.label}</span></td>
                    <td>
                      <div className="bs-pm-actions">
                        {inv.pdfUrl && (
                          <a className="bs-link" href={inv.pdfUrl} target="_blank" rel="noopener noreferrer">
                            <FiDownload /> PDF
                          </a>
                        )}
                        {inv.payUrl && (
                          <a className="bs-link bs-link--danger" href={inv.payUrl} target="_blank" rel="noopener noreferrer">
                            {inv.status === 'failed' ? 'Retry' : 'Pay'}
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="bs-pager">
        <span className="bs-muted bs-small">
          {filtered.length
            ? `Showing ${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, filtered.length)} of ${filtered.length} invoice${filtered.length === 1 ? '' : 's'}`
            : 'Showing 0 invoices'}
        </span>
        <div className="bs-pager-buttons">
          <button type="button" aria-label="Previous page" disabled={page <= 1} onClick={() => setPage(page - 1)}>
            <FiChevronLeft />
          </button>
          {Array.from({ length: pageCount }, (_, i) => i + 1).map((n) => (
            <button key={n} type="button" className={n === page ? 'active' : ''} aria-current={n === page ? 'page' : undefined} onClick={() => setPage(n)}>
              {n}
            </button>
          ))}
          <button type="button" aria-label="Next page" disabled={page >= pageCount} onClick={() => setPage(page + 1)}>
            <FiChevronRight />
          </button>
        </div>
      </div>
    </section>
  );
}
